import { Middleware } from '@reduxjs/toolkit';
import errorMiddleware from './errorMiddleware';
import connectorMiddleware from './connectorMiddleware';
import callerMiddleware from './callerMiddleware';
import calculateDevicesMiddleware from './calculateDevicesMiddleware'; // Synchronous device calculation middleware
import hearSpaceMiddleware from './hearSpaceMiddleware'; // HearSpace Acoustics Engine integration
import toastMiddleware from './toastMiddleware'; // Toast notification middleware

/**
 * Middleware pipeline — executes in this exact order per dispatched action:
 *
 *  1. errorMiddleware            — validation gate; blocks invalid actions before state changes
 *  2. connectorMiddleware        — UI state coordination (tabs, modals, area management)
 *  3. hearSpaceMiddleware        — HearSpace API calls (debounced, CRD only)
 *  4. calculateDevicesMiddleware — synchronous device layout calculations; dispatches follow-ups
 *  5. callerMiddleware           — external API calls (Warehouse / Classroom Designer only)
 *  6. toastMiddleware            — post-reducer observer; MUST be last (see note below)
 *
 * ─── Why errorMiddleware MUST be first ────────────────────────────────────────
 * errorMiddleware is the only stage that can abort an action (via early return
 * instead of calling next()). If it ran after any other middleware, those earlier
 * stages would already have fired side-effects (UI updates, API calls, calculations)
 * against invalid state before the block takes effect. For example:
 *   - connectorMiddleware would open/close tabs for a device that will be rejected
 *   - calculateDevicesMiddleware would recompute layouts for a device count that
 *     violates limits
 * Running it first ensures the pipeline short-circuits cleanly with zero side-effects
 * when an action is invalid.
 *
 * ─── Coupling: errorMiddleware ↔ calculateDevicesMiddleware ───────────────────
 * These two stages share state in two ways:
 *
 *  a) willRemoveDeviceBeBlocked() — calculateDevicesMiddleware contains a helper
 *     that mirrors errorMiddleware's removeDevice blocking logic. Before running
 *     any layout calculation, calculateDevicesMiddleware calls this function to
 *     detect actions that errorMiddleware will block downstream. If the action will
 *     be blocked, calculateDevicesMiddleware skips its work and forwards the action
 *     directly to errorMiddleware via next(). This avoids wasted calculation and
 *     prevents intermediate state from being written before the block fires.
 *
 *  b) ui.error state — errorMiddleware writes error codes into ui.error.
 *     calculateDevicesMiddleware reads ui.error after recalculation to decide
 *     whether to show the device overlay and heatmap. When errors are active,
 *     both overlays are suppressed (setShowDevices(false)) so the user sees the
 *     error state rather than potentially misleading device positions.
 *
 * ─── Why toastMiddleware MUST be last ─────────────────────────────────────────
 * toastMiddleware is a post-reducer observer: it reads _toastMessage from the
 * settled store state after the reducer runs. Placing it earlier causes
 * clearToastMessage (dispatched by calculateDevicesMiddleware after recalculation)
 * to wipe _toastMessage before toastMiddleware reads it, silently dropping toasts.
 *
 * ─── Loop prevention flags ────────────────────────────────────────────────────
 * Middleware that re-dispatches actions must attach typed meta flags to prevent
 * infinite recalculation cycles. All flags are defined and documented in
 * src/types/middleware/actionMeta.ts. Summary:
 *
 *  _fromSyncMiddleware   (MiddlewareActionMeta)
 *    Prevents calculateDevicesMiddleware from triggering recalculation on an
 *    updateDevice action that was itself dispatched by calculateDevicesMiddleware.
 *    Also suppresses the "customised" marking in the room.ts reducer.
 *
 *  _fromMiddleware       (MiddlewareActionMeta)
 *    Generic origin flag for any middleware-originated dispatch. Prevents
 *    re-entry into validation loops when middleware issues corrective actions
 *    (e.g. validation.js issuing a setMicrophones fix after height-constraint
 *    validation).
 *
 *  _triggerHearSpace     (SpeakerPayloadMeta — nested inside payload.speakers)
 *    Forces hearSpaceMiddleware to run an acoustic calculation even when
 *    _fromSyncMiddleware is set on the parent payload. Used by
 *    calculateDevicesMiddleware after it populates a previously empty speaker
 *    layout so HearSpace receives the fresh data.
 *
 *  _forceSpeakerRecalc   (SpeakerPayloadMeta — nested inside payload.speakers)
 *    Bypasses the HearSpace debounce timer for an immediate recalculation.
 *
 *  _fromHearSpace        (PlotDataMeta — nested inside payload.speakers.plotData)
 *    Marks plotData as produced by the HearSpace API. hearSpaceMiddleware skips
 *    triggering a new API call for updateDevice actions whose plotData carries
 *    this flag, preventing a redundant recalculation loop after each response.
 *    Also read by room.ts to preserve acoustic overlay data during device resets.
 *
 * Common mistake: omitting _fromSyncMiddleware on a middleware updateDevice dispatch
 * causes an infinite loop (recalc → dispatch → recalc → …) that freezes the browser.
 *
 * @see src/types/middleware/actionMeta.ts for full flag definitions and usage examples
 * @see Documentation/middleware-sequence-diagram.md for action flow diagrams
 * @see Documentation/middleware-action-flow.md for per-middleware responsibilities
 */
export const Middlewares: Middleware[] = [
  errorMiddleware,
  connectorMiddleware,
  hearSpaceMiddleware,
  calculateDevicesMiddleware,
  callerMiddleware,
  toastMiddleware,  // must be last — post-reducer observer (see note above)
];

export default Middlewares;